// Custom postal code error messages for address pages
export const POSTAL_CODE_ERROR_MESSAGES = {
  USA: {
    required: 'Please enter a postal code',
    pattern: 'Please enter a valid 5-digit postal code',
  },
  CAN: {
    required: 'Please enter a postal code',
    pattern: 'Please enter a valid 6-character postal code',
  },
  MEX: {
    required: 'Please enter a postal code',
    pattern: 'Please enter a valid 5-digit postal code',
  },
  NONE: {
    required: 'Please enter a postal code',
    pattern: 'Please enter a valid postal code',
  },
  OTHER: {
    required:
      "Please enter a postal code that meets your country's requirements. If your country doesn't require a postal code, enter NA.",
    pattern: 'Please enter a valid postal code',
  },
};

export const veteranFields = {
  parentObject: 'veteran',
  fullName: 'fullName',
  dateOfBirth: 'dateOfBirth',
  ssn: 'ssn',
  vaFileNumber: 'vaFileNumber',
  veteranServiceNumber: 'veteranServiceNumber',
  address: 'address',
  plannedAddress: 'plannedAddress',
  plannedAddressNotApplicable: 'plannedAddressNotApplicable',
  homePhone: 'homePhone',
  alternatePhone: 'alternatePhone',
  email: 'email',
  agreeToElectronicCorrespondence: 'agreeToElectronicCorrespondence',
};

export const applicationInfoFields = {
  parentObject: 'applicationInfo',
  branchOfService: 'branchOfService',
  activeDutyStatus: 'activeDutyStatus', // Veteran (false) vs Service member (true)
  currentlyOnActiveDuty: 'currentlyOnActiveDuty', // Are you currently on active duty? No (false) vs Yes (true)
  placeOfEntry: 'placeOfEntry',
  dateOfEntry: 'dateOfEntry',
  placeOfRelease: 'placeOfRelease',
  dateOfRelease: 'dateOfRelease',
  vaOfficeLocation: 'vaOfficeLocation',
  conveyanceType: 'conveyanceType',
  otherConveyanceType: 'otherConveyanceType',
  driverOrPassenger: 'driverOrPassenger',
  previouslyAppliedConveyance: 'previouslyAppliedConveyance',
  previouslyAppliedPlace: 'previouslyAppliedPlace',
  previouslyAppliedDate: 'previouslyAppliedDate',
  veteranDisabilityCompensation: 'veteranDisabilityCompensation',
  appliedDisabilityCompensationPlace: 'appliedDisabilityCompensationPlace',
  appliedDisabilityCompensationDate: 'appliedDisabilityCompensationDate',
};

export const DRIVER_OR_PASSENGER = {
  driver: 'I will be driving this vehicle',
  passenger: 'I will be the passenger only',
};

export const CONVEYANCE_TYPES = {
  automobile: 'Automobile',
  stationWagon: 'Station Wagon',
  van: 'Van',
  truck: 'Truck',
  other: 'Other (Specify)',
};

export const BRANCH_OF_SERVICE = {
  army: 'Army',
  navy: 'Navy',
  marineCorps: 'Marine Corps',
  airForce: 'Air Force',
  coastGuard: 'Coast Guard',
  spaceForce: 'Space Force',
  noaa: 'NOAA',
  usphs: 'USPHS',
};

export const FORM_21_4502 = {
  INTRODUCTION: {
    FORM_TITLE: 'Apply for automobile allowance and adaptive equipment online',
    FORM_SUBTITLE:
      'Application For Automobile or Other Conveyance and Adaptive Equipment (Under 38 U.S.C. 3901-3904) (VA Form 21-4502)',
    INTRO:
      'Use VA Form 21-4502 if you’re a Veteran or service member with qualifying disabilities and want to apply for benefits to help pay for a vehicle that meets your needs. This benefit can help you buy a vehicle or other conveyance (like a motorhome, commercial truck, or farm machine) and approved adaptive equipment so you can drive or get in and out safely.',
    STEP_ELIGIBILITY_TITLE: 'Check your eligibility',
    STEP_ELIGIBILITY_INTRO:
      'If you’re a Veteran or service member with certain service-connected disabilities resulting from an injury or disease incurred or aggravated during active military service, you may be eligible for this benefit.',
    STEP_ELIGIBILITY_CONDITION_INTRO:
      'At least one of these conditions must be true. You have one or more of the following service-connected disabilities:',
    STEP_ELIGIBILITY_BULLETS: [
      'Loss or permanent loss of use of one or both hands, or',
      'Loss or permanent loss of use of one or both feet, or',
      'Severe burn injury causing scars that limits movement of one or more limbs or the trunk and prevents you from operating a vehicle, or',
      'Amyotrophic lateral sclerosis (ALS), or',
      'Permanent vision impairment in both eyes that significantly limits your vision',
    ],
    STEP_ELIGIBILITY_ADDITIONAL_REQUIREMENTS_TITLE: 'Additional requirements',
    STEP_ELIGIBILITY_ADDITIONAL_REQUIREMENTS_INTRO:
      'If you meet the eligibility criteria, all of these must be true:',
    STEP_ELIGIBILITY_ADDITIONAL_REQUIREMENTS_BULLETS: [
      'You must receive VA approval before purchasing a vehicle or adaptive equipment, and',
      'You must answer all questions fully and accurately',
    ],
    STEP_ELIGIBILITY_ADDITIONAL_REQUIREMENTS_FOOTER:
      'Veterans and service members may apply for automobile or adaptive equipment benefits under 38 U.S.C. 3901-3904.',
    STEP_ELIGIBILITY_VISION_TRIGGER:
      'How we define permanent vision impairment',
    STEP_ELIGIBILITY_VISION_INTRO:
      'You meet this vision requirement if one of these is true, even when you use corrective glasses or lenses:',
    STEP_ELIGIBILITY_VISION_BODY: [
      'Central visual acuity of 20/200 or less in the better eye, or',
      'Central visual acuity of more than 20/200 if a visual field defect limits the widest diameter of visual field to 20 degrees or less in the better eye',
    ],
    STEP_GATHER_TITLE: 'Gather your information',
    STEP_GATHER_INTRO: "Here's what you'll need to apply:",
    STEP_GATHER_BULLETS: [
      {
        label: 'Your personal information',
        body: 'This includes your name, date of birth, contact information, and Social Security number or Military service number.',
      },
      {
        label: 'Your service information',
        body: 'This includes your branch of service, place and date you entered active duty, and place and date you left active duty (if applicable).',
      },
      {
        label: 'Application information',
        body: 'This includes type of vehicle you’re applying for and any previous VA automobile grants you may have received (if applicable).',
      },
    ],
    STEP_START_TITLE: 'Start your application',
    STEP_START_BODY:
      'We’ll take you through each step of the process. It should take about 15 minutes.',
    STEP_START_FOLLOWUP:
      'You can save your progress and return to this form later.',
    AUTH_START_FORM_TEXT: 'Start the application',
    SAVE_IN_PROGRESS_TEXT:
      'Please complete the 21-4502 form to apply for automobile or adaptive equipment compensation.',

    INFO_ALERT_TITLE: 'Adaptive equipment for vehicles you already own',
    INFO_ALERT_PARAGRAPH:
      'If you’re applying for financial help to buy adaptive equipment for a vehicle you already own, complete VA Form 10-1394 (Application for Adaptive Equipment - Motor Vehicle).',
    INFO_1034_LINK: '/forms/10-1394/',
    INFO_1034_LINK_TEXT: 'Get VA Form 10-1394 to download',
    NEED_KNOW_BEFORE_YOU_START_TITLE:
      'What to know before you fill out this form',
    NEED_KNOW_BEFORE_YOU_START_TEXT_LINE_ONE:
      "You'll need to have applied for VA disability compensation before we can approve your VA Form 21-4502. This allows us to determine if you have a qualifying service-connected disability.",
    NEED_KNOW_BEFORE_YOU_START_TEXT_LINE_TWO:
      "If you haven't applied for VA disability compensation yet, you can review eligibility for VA disability benefits or apply now.",
    FORM_21_526EZ_LINK: '/forms/21-526ez/',
    FORM_21_526EZ_LINK_TEXT:
      'File for disability compensation with VA Form 21-526EZ',
    AFTER_APPLY_TITLE: 'What happens after you apply',
    AFTER_APPLY_TEXT:
      'We’ll contact you by mail if we need more information. Once we process your application, we’ll mail you a letter with our decision.',
    AFTER_APPLY_FOLLOWUP:
      'If you don’t receive a letter from us about your request, don’t submit another request. Call us at 877-222-8387 (TTY: 711). We’re here Monday through Friday, 8:00 a.m. to 8:00 p.m. ET.',
  },
  PERSONAL_INFO: {
    TITLE: 'Veteran or service member name and date of birth',
    PAGE_DESCRIPTION:
      'We need to collect some basic information about you first.',
    DATE_OF_BIRTH: 'Date of birth',
    ERROR_DOB_INCOMPLETE: 'Please enter a complete date',
    ERROR_FIRST_NAME: "Enter the Veteran or service member's first name",
    ERROR_LAST_NAME: "Enter the Veteran or service member's last name",
    ERROR_DOB_PATTERN: 'Please enter a valid date of birth',
    ERROR_DOB_REQUIRED:
      'Please enter the Veteran or service member’s date of birth',
    ERROR_SSN_PATTERN:
      'Please enter a valid 9 digit Social Security number (dashes allowed)',
    ERROR_SSN_REQUIRED: "Please Enter the Veteran or service member's SSN",
    VA_FILE_NUMBER: 'VA file number (optional)',
    VETERAN_SERVICE_NUMBER: 'Service number (optional)',
    FULL_NAME_LABELS: {
      'first or given name': 'First name',
      'middle name': 'Middle initial',
      'last or family name': 'Last name',
    },
    REVIEW_EDIT_LABEL: 'Veteran or service member name and date of birth',
  },

  IDENTIFICATION_INFORMATION: {
    STEP_LABEL: 'Identification information',
    TITLE: 'Your identification information',
    PAGE_DESCRIPTION:
      'Enter your Social Security number to help us identify your VA records. If you have a VA file number or Veteran service number, you can enter those too.',
    ERROR_SSN_REQUIRED:
      'Enter the Veteran or service member’s Social Security number',
    REVIEW_EDIT_LABEL: 'your identification information',
  },

  CONTACT_INFO: {
    TITLE: 'Phone number and email address',
    PAGE_DESCRIPTION: 'How can we reach you?',
    PRIMARY_PHONE: 'Primary telephone number',
    ALTERNATE_PHONE: 'International phone number (if applicable)',
    ERROR_PHONE_REQUIRED:
      'Enter a valid United States of America phone number. Use 10 digits.',
    EMAIL: 'Email address',
    EMAIL_HINT:
      "We'll use this email address to confirm when we receive your form",
    ERROR_EMAIL:
      'Enter a valid email address without spaces using this format: email@domain.com',
    AGREE_ELECTRONIC:
      'I agree to receive electronic correspondence from VA about my claim.',
    ERROR_AGREE_EMAIL:
      'Enter an email address to receive electronic correspondence',
    REVIEW_EDIT_LABEL: 'your phone number and email address',
  },
  ADDRESS: {
    TITLE: 'Current mailing address',
    PAGE_DESCRIPTION:
      "This is where we'll mail important information about your claim.",
    MILITARY_CHECKBOX:
      'I live on a U.S. military base outside of the United States.',
    STREET: 'Street address',
    ERROR_STREET_REQUIRED:
      'Enter the Veteran or service member’s street address',
    STREET2: 'Apartment or unit number',
    CITY: 'City',
    STATE: 'State',
    POSTAL_CODE: 'Postal code',
    REVIEW_EDIT_LABEL: 'your current mailing address',
  },
  PLANNED_ADDRESS: {
    TITLE: 'What is your planned address after release from active duty?',
    PAGE_DESCRIPTION:
      "Enter the address you'll use after you're released from active duty.",
    NOT_APPLICABLE: 'Not applicable',
    ERROR_REQUIRED:
      'To continue, enter your planned address after release from active duty or select “Not applicable”',
    ERROR_STREET_REQUIRED: 'Enter the service member’s planned street address',
    STREET2: 'Apartment or unit number (optional)',
    REVIEW_EDIT_LABEL:
      'service member’s planned address after release from active duty',
  },
  APPLICATION_INFORMATION: {
    TITLE: 'Your service information',
    PAGE_DESCRIPTION:
      'We need information about your service record to evaluate your application.',
    BRANCH_OF_SERVICE: 'Branch of service',
    ERROR_BRANCH_OF_SERVICE: 'Please select your branch of service',
    PLACE_OF_ENTRY: 'Place of entry into active duty',
    ERROR_PLACE_OF_ENTRY: 'Please enter the place you entered into active duty',
    DATE_OF_ENTRY: 'Date of entry into active duty',
    HINT_DATE_OF_ENTRY:
      'Select a month. Enter 1 or 2 digits for the day and 4 digits for the year.',
    ERROR_DATE_ENTRY_REQUIRED:
      'Please provide date of entry into active duty, include the 4-digit year',
    ERROR_DATE_ENTRY_INCOMPLETE: 'Please enter a complete date',
    ERROR_DATE_ENTRY_INVALID:
      'Please enter a valid date of entry into active duty',
    ERROR_DATE_ENTRY_AFTER_RELEASE: releaseDate =>
      `Date of entry into active duty can’t be after the date of release from active duty, ${releaseDate}`,
    REVIEW_EDIT_LABEL: 'your service information',
  },
  CURRENT_SERVICE_STATUS: {
    STEP_LABEL: 'Service status',
    TITLE: 'Your current service status',
    QUESTION: 'Are you currently on active duty?',
    LABELS: { Y: 'Yes', N: 'No' },
    ERROR:
      'Select either “Yes” or “No” to indicate if you are currently on active duty',
    REVIEW_EDIT_LABEL: 'your current service status',
  },
  VETERAN_STATUS_INFORMATION: {
    STEP_LABEL: 'Veteran service record',
    TITLE: 'Your release from active duty',
    PLACE_OF_RELEASE: 'Place of release from active duty',
    ERROR_PLACE_OF_RELEASE:
      'Enter the city and state or base where you were released from active duty',
    DATE_OF_RELEASE: 'Date of release from active duty',
    HINT_DATE_OF_RELEASE:
      'Select a month. Select or enter 1 or 2 digits for the day. Enter 4 digits for the year.',
    ERROR_DATE_RELEASE_REQUIRED:
      'Please provide the date of release from active duty, include the 4-digit year',
    ERROR_DATE_RELEASE_INCOMPLETE: 'Please enter a complete date',
    ERROR_DATE_RELEASE_INVALID: 'Please enter a valid date',
    ERROR_DATE_RELEASE_BEFORE_ENTRY: entryDate =>
      `Date of release from active duty must be the same as or later than date of entry into active duty, ${entryDate}`,
    REVIEW_EDIT_LABEL: 'Veteran’s release from active duty information',
  },
  VETERAN_DISABILITY_COMPENSATION: {
    TITLE: 'Your disability compensation application information',
    QUESTION_APPLIED:
      'Have you previously applied for VA disability compensation?',
    LABELS: { N: 'No', Y: 'Yes' },
    ERROR_APPLIED:
      'Select either "Yes" or "No" to tell us if you have previously applied for VA disability benefits',
    TITLE_IF_YES: 'If "Yes", specify name and place',
    ERROR_IF_YES:
      'Enter the name of the place where you applied for disability benefits',
    DATE_APPLIED: 'Date you applied',
    HINT_DATE_APPLIED:
      'Select a month. Enter 1 or 2 digits for the day and 4 digits for the year.',
    ERROR_DATE_APPLIED_REQUIRED:
      'Enter the date you applied for disability benefits',
    ERROR_DATE_APPLIED_INCOMPLETE: 'Please enter a complete date',
    ERROR_DATE_APPLIED_INVALID: 'Please enter a valid date',
    VA_OFFICE_LOCATION: 'Location of VA Office that has your file (if known)',
    REVIEW_EDIT_LABEL: 'your disability compensation application information',
  },
  VEHICLE_DETAILS: {
    TITLE: 'Vehicle details',
    PAGE_DESCRIPTION:
      'Provide details about the automobile or conveyance (vehicle) you are applying for.',
    QUESTION_TYPE:
      'What type of automobile or conveyance are you applying for?',
    ERROR_TYPE: 'Select the type of conveyance (vehicle) you are applying for',
    OTHER_SPECIFY:
      'If "Other", please specify (For example motorhomes, commercial trucks, or farm machines, such as tractor, harvester, or combine)',
    ERROR_OTHER:
      'Please enter the type of motorized vehicle you are applying for',
    REVIEW_EDIT_LABEL:
      'the type of automobile or conveyance you are applying for.',
  },
  VEHICLE_USE: {
    TITLE: 'Driver or passenger?',
    DESCRIPTION:
      'Let us know if you will be driving this vehicle or riding as a passenger only.',
    QUESTION: 'Will you be the driver or the passenger of this vehicle?',
    HINT: 'Select "I will be driving this vehicle" if you will drive this vehicle at any time.',
    ERROR:
      'Select whether you will drive the vehicle or only ever ride as a passenger',
    REVIEW_EDIT_LABEL:
      'whether you will be a driver or passenger of this vehicle.',
  },
  PREVIOUS_VEHICLE_APPLICATION: {
    TITLE: 'Previous vehicle application information',
    PAGE_DESCRIPTION:
      'Tell us if you’ve previously applied for an automobile (such as a car, truck, or van) or another conveyance (such as a motorhome or farm machine).',
    QUESTION_PREVIOUSLY_APPLIED:
      'Have you previously applied for an automobile or other conveyance?',
    LABELS_PREVIOUSLY_APPLIED: {
      N: 'No',
      Y: 'Yes (enter the date and place of your previous application)',
    },
    ERROR_PREVIOUSLY_APPLIED:
      'Select either “Yes” or “No” to tell us if you have previously applied for a vehicle',
    TITLE_PREVIOUSLY_APPLIED_DATE: 'Previous vehicle application date',
    TITLE_PREVIOUSLY_APPLIED_PLACE: 'Previous vehicle application place',
    HINT_DATE:
      'Select a month. Enter 1 or 2 digits for the day and 4 digits for the year.',
    ERROR_DATE_PATTERN: 'Please enter a valid date',
    ERROR_DATE_INCOMPLETE: 'Please enter a complete date',
    ERROR_DATE_REQUIRED:
      'Enter the date of your prior vehicle application, include the 4-digit year',
    ERROR_PLACE_REQUIRED:
      'Tell us where you submitted your previous vehicle application',
    REVIEW_EDIT_LABEL: 'your previous vehicle application information',
  },
  CERTIFICATION: {
    TITLE: 'Application certification statements and acknowledgements',
    PAGE_DESCRIPTION:
      'Select each required checkbox to certify or acknowledge that you have read and understand the information before continuing.',
    PENALTY_HEADING: 'Penalty',
    PENALTY_TEXT:
      'The law provides severe penalties, which include fine or imprisonment or both, for the willful submission of any statement or evidence of a material fact, knowing it to be false, or for the fraudulent acceptance of any payment to which you are not entitled.',
    CERTIFY_LICENSING_TITLE:
      'I have read and understand the Application for Conveyance and Equipment certification.',
    CERTIFY_LICENSING_HEADING:
      'Application for Conveyance and Equipment certification',
    CERTIFY_LICENSING_DESCRIPTION:
      'I hereby apply for the conveyance selected and the equipment required because of my disability. I agree that before operating the vehicle I shall hereafter apply to the proper authority for the necessary license to operate it. If I am unable to qualify for a license, I certify that a person licensed to operate a similar vehicle in the state of my residence will operate the vehicle for me.',
    CERTIFY_NO_PRIOR_TITLE:
      'I have read and understand the Automobile Grant Eligibility certification.',
    CERTIFY_NO_PRIOR_HEADING: 'Automobile Grant eligibility certification',
    CERTIFY_NO_PRIOR_DESCRIPTION:
      'I further certify that VA has not previously paid an automobile grant on my behalf or that either (1) the automobile previously purchased with assistance was destroyed as a result of a natural or other disaster, or (2) it has been 30 or more years since my most recent automobile grant.',
    CERTIFY_ADAPTIVE_GRANT_TITLE:
      'I have read and understand the Adaptive Equipment acknowledgement.',
    CERTIFY_ADAPTIVE_GRANT_HEADING: 'Adaptive Equipment acknowledgement',
    CERTIFY_ADAPTIVE_GRANT_DESCRIPTION:
      'I understand that I must contact my local Veterans Health Administration (VHA) Prosthetic and Sensory Aids Service prior to obtaining any (new or used) adaptive equipment and that VA may deny claims for payment or reimbursements if eligibility has not been established or has been terminated.',
    ERROR_LICENSING:
      'Select the checkbox to certify that you are applying for a vehicle because of your disability and the driver has a valid license.',
    ERROR_NO_PRIOR:
      'Select the checkbox to certify your eligibility for an automobile grant.',
    ERROR_ADAPTIVE_GRANT:
      'Select the checkbox to confirm you have read and understand the Adaptive Equipment Acknowledgement.',
    REVIEW_EDIT_LABEL:
      'required application certifications and acknowledgements',
  },
  COMMON: {
    ERROR_DATE_VALID: 'Please provide a valid date',
    HINT_DATE:
      'Select a month. Enter 1 or 2 digits for the day and 4 digits for the year.',
  },
  FORM_CONFIG: {
    TITLE:
      'Application for automobile or other conveyance and adaptive equipment (Under 38 U.S.C. 3901-3904) (VA 21-4502)',
    CHAPTER_VETERAN_ID: 'Basic information',
    CHAPTER_CONTACT: 'Contact information',
    CHAPTER_ADDRESS: 'Mailing address',
    CHAPTER_SERVICE_STATUS: 'Service member’s planned address',
    CHAPTER_APPLICATION: 'Service information',
    CHAPTER_QUALIFYING: 'Prior disability compensation application',
    CHAPTER_SERVICE_RECORD: 'Service status',
    CHAPTER_CONVEYANCE: 'Vehicle type',
    CHAPTER_VEHICLE_USE: 'Vehicle use',
    CHAPTER_PREVIOUS_VEHICLE: 'Vehicle application history',
    CHAPTER_CERTIFICATION: 'Required certifications and acknowledgements',
    PAGE_PERSONAL_INFO: 'Veteran or service member name and date of birth',
    PAGE_CONTACT: 'Phone number and email address',
    PAGE_ADDRESS: 'Current mailing address',
    PAGE_PLANNED_ADDRESS:
      'What is your planned address after release from active duty?',
    PAGE_APPLICATION_INFO: 'Your service information',
    PAGE_CURRENT_SERVICE: 'Your current service status',
    PAGE_VETERAN_STATUS: 'Your release from active duty',
    PAGE_DISABILITY: 'Your disability compensation application information',
    PAGE_VEHICLE_DETAILS: 'Vehicle details',
    PAGE_VEHICLE_USE: 'Driver or passenger?',
    PAGE_PREVIOUS_VEHICLE: 'Previous vehicle application information',
    PAGE_CERTIFICATION:
      'Application certification statements and acknowledgements',
  },
};
